const MAX_RETRY = 2;
const MAX_PARALLEL = 2;

function withRetryQuery(url: string, attempt: number): string {
  if (/[?&]retry=\d+/.test(url)) {
    return url.replace(/([?&]retry=)\d+/, `$1${attempt}`);
  }
  return `${url}${url.includes('?') ? '&' : '?'}retry=${attempt}`;
}

type LoadScriptFn = (
  url: string,
  done: (event?: Event) => void,
  key?: string,
  chunkId?: unknown,
) => void;

type ScriptJob = {
  url: string;
  done: (event?: Event) => void;
  key?: string;
  chunkId?: unknown;
  attempt: number;
};

/**
 * Cloudflare HTTP/3 stalls when many JS files start at once (antd sat pending
 * ~50s). Load at most two chunk scripts at a time.
 */
export function installChunkScriptRetry(): void {
  if (typeof window === 'undefined') return;

  const tryPatch = (): boolean => {
    const webpackRequire = (
      window as Window & {
        __webpack_require__?: {
          l?: LoadScriptFn & { __kkRetry?: boolean };
        };
      }
    ).__webpack_require__;

    const loadScript = webpackRequire?.l;
    if (!loadScript) return false;
    if (loadScript.__kkRetry) return true;

    const original = loadScript.bind(webpackRequire) as LoadScriptFn;
    const queue: ScriptJob[] = [];
    let active = 0;

    const pump = () => {
      while (active < MAX_PARALLEL && queue.length > 0) {
        const job = queue.shift();
        if (!job) return;
        active += 1;
        const src =
          job.attempt > 0 ? withRetryQuery(job.url, job.attempt) : job.url;
        original(
          src,
          (event) => {
            active = Math.max(0, active - 1);
            const failed = Boolean(event && event.type === 'error');
            if (failed && job.attempt < MAX_RETRY) {
              queue.unshift({ ...job, attempt: job.attempt + 1 });
            } else {
              job.done(event);
            }
            pump();
          },
          job.key,
          job.chunkId,
        );
      }
    };

    const patched: LoadScriptFn & { __kkRetry?: boolean } = function (
      url,
      done,
      key,
      chunkId,
    ) {
      queue.push({ url, done, key, chunkId, attempt: 0 });
      pump();
    };

    patched.__kkRetry = true;
    webpackRequire.l = patched;
    return true;
  };

  if (!tryPatch()) {
    const id = window.setInterval(() => {
      if (tryPatch()) window.clearInterval(id);
    }, 20);
    window.setTimeout(() => window.clearInterval(id), 5000);
  }
}
